import { useDispatch, useSelector } from 'react-redux';
import { selectAppliedOffers, selectEligibleOffers, setOffers } from '../../../store/slices/OfferSlice';
import { useEffect } from 'react';
import { CompleteBasketItem } from '../../../providers/BasketProvider';
import { selectHasCachedOrderPromoCode } from '../../../store/slices/OrderSlice';
import { haveSameOffers } from '../utils';

const useAutoApplyBestOffer = (items: CompleteBasketItem[]) => {
  const eligibleOffers = useSelector(selectEligibleOffers);
  const appliedOffers = useSelector(selectAppliedOffers);
  const hasPromoCode = useSelector(selectHasCachedOrderPromoCode);
  const dispatch = useDispatch();

  const applyBestOfferHandler = (): void => {
    // Without this guard the effect re-writes appliedOffers on every run. The payload is a new
    // array each time and `setOffers` does not compare, so BasketProvider re-renders and its
    // `items` memo returns a fresh array while any offer is applied — which re-runs this effect
    // through its `items` dependency, forever. React eventually throws "Maximum update depth
    // exceeded" from whichever child happens to setState in an effect.
    if (haveSameOffers(eligibleOffers, appliedOffers)) {
      return;
    }

    dispatch(setOffers({ appliedOffers: eligibleOffers }));
  };

  useEffect(() => {
    // A promo code is mutually exclusive with offers — don't auto-(re)apply an offer while one is
    // active. An offer and a points redemption CAN be combined (CTO-confirmed 2026-08-03), so
    // points do not block auto-apply.
    if (hasPromoCode) {
      return;
    }

    applyBestOfferHandler();
  }, [items, eligibleOffers, appliedOffers, hasPromoCode]);
};

export default useAutoApplyBestOffer;
